/**
 * ClaimTripPage — the landing page for the claim link in a managed attendee's
 * invitation email (§ G7).
 *
 * Sits behind ProtectedRoute deliberately: claiming assigns a trip to an
 * ACCOUNT, so there has to be one. An unauthenticated visitor is sent to log in
 * and returns here with the token still in the URL.
 */
import { useEffect, useRef, useState } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { travelGroupApi } from '@/lib/api';

type State = 'claiming' | 'done' | 'no_token' | 'failed';

export default function ClaimTripPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');
  const [state, setState] = useState<State>(token ? 'claiming' : 'no_token');
  const [error, setError] = useState('');
  // A claim token is single-use; StrictMode double-mounting must not spend it
  // twice and turn a success into "no longer valid".
  const attempted = useRef(false);

  useEffect(() => {
    if (!token || attempted.current) return;
    attempted.current = true;
    travelGroupApi
      .claim(token)
      .then((r) => {
        setState('done');
        navigate(`/trips/${r.trip_id}`, { replace: true });
      })
      .catch((e: any) => {
        setState('failed');
        setError(e?.response?.data?.error || e.message);
      });
  }, [token, navigate]);

  return (
    <div className="mx-auto flex max-w-lg flex-col gap-3 p-8 text-center">
      {state === 'claiming' && <p className="text-sm text-muted-foreground">Setting up your trip…</p>}

      {state === 'no_token' && (
        <p className="text-sm text-muted-foreground">
          This link is missing its code. Ask whoever invited you to send it again.
        </p>
      )}

      {state === 'failed' && (
        <>
          <p className="text-sm text-destructive">{error || 'That invitation is no longer valid.'}</p>
          {/* A spent token is the most likely cause and it is not a failure — it
              usually means they already claimed it, possibly on another device. */}
          <p className="text-xs text-muted-foreground">
            If you already took over this trip, it's on your dashboard.
          </p>
          <Link to="/dashboard" className="text-xs underline">
            Go to dashboard
          </Link>
        </>
      )}
    </div>
  );
}
